<script setup>
import { PerfectScrollbar } from 'vue3-perfect-scrollbar'
import { VNodeRenderer } from './VNodeRenderer'
import {
  injectionKeyIsVerticalNavHovered,
  useLayouts,
} from '@layouts'
import {
  VerticalNavGroup,
  VerticalNavLink,
  VerticalNavSectionTitle,
} from '@layouts/components'
import { config } from '@layouts/config'

const props = defineProps({
  tag: {
    type: [
      String,
      null,
    ],
    required: false,
    default: 'aside',
  },
  navItems: {
    type: null,
    required: true,
  },
  isOverlayNavActive: {
    type: Boolean,
    required: true,
  },
  toggleIsOverlayNavActive: {
    type: Function,
    required: true,
  },
})

const refNav = ref()
const { width: windowWidth } = useWindowSize()
const isHovered = useElementHover(refNav)

provide(injectionKeyIsVerticalNavHovered, isHovered)

const {
  isVerticalNavCollapsed: isCollapsed,
  isLessThanOverlayNavBreakpoint,
  isVerticalNavMini,
  isAppRtl,
} = useLayouts()

const hideTitleAndIcon = isVerticalNavMini(windowWidth, isHovered)

const resolveNavItemComponent = item => {
  if ('heading' in item)
    return VerticalNavSectionTitle
  if ('children' in item)
    return VerticalNavGroup

  return VerticalNavLink
}

const route = useRoute()

watch(() => route.name, () => {
  props.toggleIsOverlayNavActive(false)
})

const isVerticalNavScrolled = ref(false)
const mouseEntered = ref(false)
const updateIsVerticalNavScrolled = val => isVerticalNavScrolled.value = val

const handleNavScroll = evt => {
  isVerticalNavScrolled.value = evt.target.scrollTop > 0
}
</script>

<template>
  <Component
    :is="props.tag"
    ref="refNav"
    class="layout-vertical-nav"
    :class="[
      {
        'overlay-nav': isLessThanOverlayNavBreakpoint(windowWidth),
        'hovered': isHovered,
        'visible': isOverlayNavActive,
        'scrolled': isVerticalNavScrolled,
      },
    ]"

    @mouseenter="mouseEntered = true"
    @mouseleave="mouseEntered = false"
  >
    <!-- 👉 Header -->
    <div
        class="nav-header"
    >
      <slot name="nav-header">
        <!-- 👉 Left: Collapse Icon (only show when not collapsed and not on mobile) -->
        <!-- 👉 Mobile: Close button -->
        <Component
          v-if="isLessThanOverlayNavBreakpoint(windowWidth)"
          :is="config.app.iconRenderer || 'div'"
          class="header-action"
          v-bind="config.icons.close"
          @click="toggleIsOverlayNavActive(false)"
        />

        <RouterLink
          to="/"
          class="app-logo d-flex align-center gap-x-3 app-title-wrapper"
        >
          <VNodeRenderer :nodes="config.app.logo" />

          <Transition name="vertical-nav-app-title">
            <h1
              v-show="!hideTitleAndIcon"
              class="app-title leading-normal"
            >
              {{ config.app.title }}
            </h1>
          </Transition>
        </RouterLink>
          <Component
            v-if="!isLessThanOverlayNavBreakpoint(windowWidth) && !isCollapsed"
            :is="config.app.iconRenderer || 'div'"
            class="header-action header-collapse-icon"
            v-bind="{ icon: 'tabler-chevron-left', size: 24 }"
            @click="isCollapsed = true"
          />

      </slot>
    </div>
    <slot name="before-nav-items">
<!--      <div class="vertical-nav-items-shadow" />-->
    </slot>
    <slot
      name="nav-items"
      :update-is-vertical-nav-scrolled="updateIsVerticalNavScrolled"
    >
      <PerfectScrollbar
        :key="isAppRtl"
        tag="ul"
        class="nav-items"
        :options="{ wheelPropagation: false }"
        @ps-scroll-y="handleNavScroll"
      >
        <Component
          :is="resolveNavItemComponent(item)"
          v-for="(item, index) in navItems"
          :key="index"
          :item="item"
        />
      </PerfectScrollbar>
    </slot>
  </Component>
</template>

<style lang="scss">
@use "@configured-variables" as variables;
@use "@layouts/styles/mixins";

// Override gradient background - must be outside .layout-vertical-nav to match the selector specificity
.layout-nav-type-vertical .layout-vertical-nav .nav-link > .router-link-exact-active {
  background: #f1f5f9 !important;
  background-color: #f1f5f9 !important;
  background-image: none !important;
}

// 👉 Vertical Nav
.layout-vertical-nav {
  background-color: rgb(var(--v-theme-primary)) !important;
  position: fixed;
  z-index: variables.$layout-vertical-nav-z-index;
  display: flex;
  flex-direction: column;
  block-size: 100%;
  inline-size: variables.$layout-vertical-nav-width;
  inset-block-start: 0;
  inset-inline-start: 0;
  transition: transform 0.25s ease-in-out, inline-size 0.25s ease-in-out, box-shadow 0.25s ease-in-out;
  will-change: transform, inline-size;

  // Override gradient background for active nav links
  .nav-link > .router-link-exact-active {
    background: #f1f5f9 !important;
    background-color: #f1f5f9 !important;
    background-image: none !important;
  }

  .nav-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;

    .header-action {
      cursor: pointer;
      flex-shrink: 0;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .header-collapse-icon {
      margin-inline-end: 0;
    }
  }

  .app-title-wrapper {
    margin-inline-end: auto;
  }

  .app-title {
    font-size: 18px;
    font-weight: 600;
    color: #fff;
  }

  .nav-items {
    block-size: 100%;
    padding-top: 5px;
    background-color: rgb(var(--v-theme-primary));

    // ℹ️ We no loner needs this overflow styles as perfect scrollbar applies it
    // overflow-x: hidden;

    // // ℹ️ We used `overflow-y` instead of `overflow` to mitigate overflow x. Revert back if any issue found.
    // overflow-y: auto;
  }

  .nav-link,
  .nav-group {
    > a,
    > :first-child,
    .nav-group-label {
      color: #fff;
      height: 36px;
    }

    .nav-item-icon {
      color: #fff;
  }

  .nav-item-title {
    overflow: hidden;
    margin-inline-end: auto;
    text-overflow: ellipsis;
    white-space: nowrap;
      color: #fff;
    }

    .nav-group-arrow {
      color: #fff;
    }
  }

  .nav-link {
    > .router-link-exact-active {
      background: #f1f5f9 !important;
      background-color: #f1f5f9 !important;
      background-image: none !important;
      color: #0F172A !important;
      box-shadow: none !important;

      .nav-item-icon {
        color: #0F172A !important;
      }

      .nav-item-title {
        color: #0F172A !important;
      }
    }

    // Additional override for the gradient selector
    > a.router-link-exact-active {
      background: #f1f5f9 !important;
      background-color: #f1f5f9 !important;
      background-image: none !important;
    }
  }

  .nav-group {
    &.active,
    &.open {
      > :first-child,
      .nav-group-label {
        background: #f1f5f9 !important;
        color: #0F172A !important;

        .nav-item-icon {
          color: #0F172A !important;
        }

        .nav-item-title {
          color: #0F172A !important;
        }

        .nav-group-arrow {
          color: #0F172A !important;
        }
      }
    }
  }

  // 👉 Collapsed
  .layout-vertical-nav-collapsed &:not(.overlay-nav) {
    inline-size: 0;
    transform: translateX(-100%);
    pointer-events: none;

    .nav-header {
      display: none;
    }
  }

  // 👉 Overlay nav
  &.overlay-nav {
    &:not(.visible) {
      transform: translateX(-#{variables.$layout-vertical-nav-width});

      @include mixins.rtl {
        transform: translateX(variables.$layout-vertical-nav-width);
      }
    }
  }
}
</style>
